import { CustomEventWithDetail, html, on, View } from 'rune-ts';
import klass from './Pagination.module.scss';
import { typo } from '../../../../shared/typography/typo';
import { PaginationData, PaginationOption } from '../../../../features/Pagination/type';
import { PageFirstIcon, PageLastIcon, PageNextIcon, PagePrevIcon } from '../../atoms/Icon';
import { htmlIf } from '../../../../shared/util';

const Action = {
  GroupPrev: 'GroupPrev',
  PagePrev: 'PagePrev',
  PageNext: 'PageNext',
  GroupNext: 'GroupNext',
} as const;

export class PaginationChangeEvent extends CustomEventWithDetail<PaginationData> {}

export class Pagination extends View<PaginationData> {
  // UI 일관성을 위해 옵션에서 제거했습니다.
  // 디자인팀과 협의 수 수정 바랍니다.
  hide_nav_on_single_group: Readonly<boolean> = true;
  is_click_disabled: boolean;

  constructor(
    data: PaginationData,
    private option: PaginationOption = {},
  ) {
    super(data, option);
    this.is_click_disabled = false;
  }

  override template() {
    const offset = this.getCurOffset();
    const length = Math.min(this.data.total - this.data.size * offset, this.data.size);

    return html`<div
      class="${this.option.klass} ${klass.pagination} ${typo('14_medium')} ${htmlIf(
        klass.hidden,
        this.isHidden,
      )}"
    >
      <span
        class="${klass.navigations} ${klass.prev} ${htmlIf(
          klass.hidden,
          Boolean(this.isSingleGroup && this.hide_nav_on_single_group),
        )}"
      >
        <button
          data-action=${Action.GroupPrev}
          class="${klass.navigation} ${htmlIf(klass.disabled, !this.canGroupPrev())}"
        >
          ${PageFirstIcon}
        </button>
        <button
          data-action=${Action.PagePrev}
          class="${klass.navigation} ${htmlIf(klass.disabled, !this.canPagePrev())}"
        >
          ${PagePrevIcon}
        </button>
      </span>

      <span class="${klass.pages}">
        ${Array.from({ length }).map((_, i) => {
          const idx = i + 1;

          return html`<button
            data-page=${offset * this.data.size + idx}
            class="${klass.page} ${this.data.page === offset * this.data.size + idx ? klass.active : ''}"
          >
            ${offset * this.data.size + idx}
          </button>`;
        })}
      </span>

      <span
        class="${klass.navigations} ${klass.next} ${htmlIf(
          klass.hidden,
          Boolean(this.isSingleGroup && this.hide_nav_on_single_group),
        )}"
      >
        <button
          data-action="${Action.PageNext}"
          class="${klass.navigation} ${htmlIf(klass.disabled, !this.canPageNext())}"
        >
          ${PageNextIcon}
        </button>
        <button
          data-action=${Action.GroupNext}
          class="${klass.navigation} ${htmlIf(klass.disabled, !this.canGroupNext())}"
        >
          ${PageLastIcon}
        </button>
      </span>
    </div>`;
  }

  @on('click', `.${klass.page}`)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private _onPageClick(e) {
    if (this.is_click_disabled) {
      return;
    }

    const selected_page = parseInt(e.currentTarget.dataset.page);
    if (this.data.page === selected_page) {
      return;
    }
    this.setPage(selected_page);
    this.redraw();
    this.dispatchEvent(PaginationChangeEvent, {
      bubbles: true,
      detail: this.data,
    });
  }

  @on('click', `.${klass.navigation}`)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  private _onNavClick(e) {
    if (this.is_click_disabled) {
      return;
    }

    const action: keyof typeof Action = e.currentTarget.dataset.action;
    if (action === Action.GroupPrev) {
      this.moveGroupPrev();
    } else if (action === Action.PagePrev) {
      this.movePagePrev();
    } else if (action === Action.PageNext) {
      this.movePageNext();
    } else if (action === Action.GroupNext) {
      this.moveGroupNext();
    }
    this.redraw();
    this.dispatchEvent(PaginationChangeEvent, {
      bubbles: true,
      detail: this.data,
    });
  }

  static calcTotal(option: { total_count: number; limit_per_page: number }): number {
    const { total_count, limit_per_page } = option;
    return Math.ceil(total_count / limit_per_page);
  }

  setPage(page: number): Readonly<PaginationData> {
    if (page < 0) {
      throw new Error('`data.page`는 0 이상이어야 합니다.');
    }
    if (page === 0) {
      this.data.page = 1;
      return this.data;
    }
    if (page > this.data.total) {
      this.data.page = this.data.total;
      return this.data;
    }
    this.data.page = page;
    return this.data;
  }

  setSize(size: number): Readonly<PaginationData> {
    if (size < 1) {
      throw new Error('`data.size`는 1 이상이어야 합니다.');
    }
    this.data.size = size;
    return this.data;
  }

  setTotal(total: number): Readonly<PaginationData> {
    if (total < 0) {
      throw new Error('`data.total`은 0 이상이어야 합니다.');
    }
    this.data.total = total;
    this.setPage(this.data.page);
    return this.data;
  }

  setData(data: PaginationData): Readonly<PaginationData> {
    this.setSize(data.size);
    this.setPage(data.page);
    this.setTotal(data.total);
    return this.data;
  }

  disableClick() {
    this.is_click_disabled = true;
  }

  enableClick() {
    this.is_click_disabled = false;
  }

  /**
   * pagination 컴포넌트는 전체 페이지가 하나일 때 숨겨집니다.
   * 마플샵 2.0 서비스 전체에 해당하는 내용이니 사용처에서 클래스로 컨트롤 하지 않도록 합니다.
   */
  get isHidden() {
    return this.data.page === 0 || this.data.total <= 1;
  }

  private get isSingleGroup() {
    return this.data.total <= this.data.size;
  }

  private getCurOffset() {
    return Math.floor((this.data.page - 1) / this.data.size);
  }

  private canPagePrev() {
    return this.data.page > 1;
  }

  private canPageNext() {
    return this.data.page < this.data.total;
  }

  private movePagePrev() {
    if (!this.canPagePrev()) {
      return;
    }
    this.data.page -= 1;
  }

  private movePageNext() {
    if (!this.canPageNext()) {
      return;
    }
    this.data.page += 1;
  }

  private canGroupPrev() {
    return this.data.page > this.data.size;
  }

  private canGroupNext() {
    const offset = this.getCurOffset();
    return this.data.total - (offset + 1) * this.data.size > 0;
  }

  private moveGroupPrev() {
    if (!this.canGroupPrev()) {
      return;
    }
    const offset = this.getCurOffset();
    this.data.page = (offset - 1) * this.data.size + 1;
  }

  private moveGroupNext() {
    if (!this.canGroupNext()) {
      return;
    }
    const offset = this.getCurOffset();
    this.data.page = (offset + 1) * this.data.size + 1;
  }
}
